import { createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Link, useNavigate } from "@tanstack/solid-router";
import { ArrowRightLeft, ChartPie, TrendingDown, TrendingUp, Wallet } from "@fleet/icons";
import { accountingMonthLabelDate, accountingMonthRange, formatMoney } from "@cashsync/shared";
import {
  useFinanceSettings,
  usePeriodSummaryQuery,
  useWalletBalancesQuery,
  useWalletsQuery,
} from "@cashsync/shared/solid";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, PageContainer, PageHeader } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import { useFinanceDeps } from "@/features/finance/hooks/use-finance-deps";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

/** Staggered entrance delay per account row, capped so long lists stay snappy. */
const ROW_STAGGER_MS = 40;
const ROW_STAGGER_MAX_MS = 240;

/** Financial dashboard: accounting month summary + balances per account. */
export function FinanceDashboard() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const deps = useFinanceDeps();
  const settings = useFinanceSettings(deps);

  const range = createMemo(() => accountingMonthRange(new Date(), settings.monthStartDay()));

  const wallets = useWalletsQuery({ ...deps, staleTime: OFFLINE_CACHE_STALE_TIME_MS });
  const balances = useWalletBalancesQuery({ ...deps, staleTime: OFFLINE_CACHE_STALE_TIME_MS });
  const summary = usePeriodSummaryQuery({
    ...deps,
    fromISO: () => range().fromISO,
    toISO: () => range().toISO,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const monthLabel = createMemo(() =>
    new Intl.DateTimeFormat(locale(), { month: "long", year: "numeric" }).format(
      accountingMonthLabelDate(range()),
    ),
  );

  const baseCurrency = createMemo(() => settings.baseCurrency());

  const totals = createMemo(() => {
    const row = summary.data?.[0];
    const expense = row?.expense ?? 0;
    const income = row?.income ?? 0;
    return { expense, income, balance: income - expense };
  });

  const balanceByWallet = createMemo(() => {
    const map = new Map<string, number>();
    for (const row of balances.data ?? []) {
      map.set(row.wallet_id, row.balance);
    }
    return map;
  });

  const visibleWallets = createMemo(() => (wallets.data ?? []).filter((w) => !w.archived));

  return (
    <PageContainer maxWidth="4xl" class="space-y-6" containerClass="min-h-0">
      <PageHeader title={t(msg`Dashboard`)} description={monthLabel()} />

      {/* ── Resumen del mes ───────────────────────────────────────── */}
      <div class="grid gap-4 sm:grid-cols-3">
        <Card class="animate-scale-in transition-transform duration-200 hover:-translate-y-0.5">
          <CardHeader class="flex flex-row items-center justify-between pb-2">
            <CardTitle class="text-sm font-medium text-muted-foreground">
              {t(msg`Income`)}
            </CardTitle>
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-success/10 text-success">
              <TrendingUp class="h-4 w-4" />
            </span>
          </CardHeader>
          <CardContent>
            <p class="text-2xl font-bold text-success font-numeric">
              {formatMoney(totals().income, baseCurrency(), locale())}
            </p>
          </CardContent>
        </Card>

        <Card
          class="animate-scale-in transition-transform duration-200 hover:-translate-y-0.5"
          style={{ "animation-delay": "60ms" }}
        >
          <CardHeader class="flex flex-row items-center justify-between pb-2">
            <CardTitle class="text-sm font-medium text-muted-foreground">
              {t(msg`Expenses`)}
            </CardTitle>
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <TrendingDown class="h-4 w-4" />
            </span>
          </CardHeader>
          <CardContent>
            <p class="text-2xl font-bold text-destructive font-numeric">
              {formatMoney(totals().expense, baseCurrency(), locale())}
            </p>
          </CardContent>
        </Card>

        <Card
          class="animate-scale-in transition-transform duration-200 hover:-translate-y-0.5"
          style={{ "animation-delay": "120ms" }}
        >
          <CardHeader class="flex flex-row items-center justify-between pb-2">
            <CardTitle class="text-sm font-medium text-muted-foreground">
              {t(msg`Balance`)}
            </CardTitle>
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <ArrowRightLeft class="h-4 w-4" />
            </span>
          </CardHeader>
          <CardContent>
            <p
              class="text-2xl font-bold font-numeric"
              classList={{
                "text-success": totals().balance > 0,
                "text-destructive": totals().balance < 0,
              }}
            >
              {formatMoney(totals().balance, baseCurrency(), locale(), {
                signDisplay: "exceptZero",
              })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Saldos por cuenta ─────────────────────────────────────── */}
      <Card>
        <CardHeader class="flex flex-row items-center justify-between">
          <CardTitle class="text-base">{t(msg`Accounts`)}</CardTitle>
          <Link to="/accounts" class="text-xs text-primary hover:underline">
            {t(msg`Manage accounts`)}
          </Link>
        </CardHeader>
        <CardContent>
          <Show
            when={visibleWallets().length > 0}
            fallback={
              <EmptyState
                class="p-4"
                icon={Wallet}
                title={t(msg`No accounts yet`)}
                description={t(msg`Create your first account to start tracking.`)}
                iconActionLabel={t(msg`Create your first account`)}
                onIconActivate={() => void navigate({ to: "/accounts" })}
                action={
                  <Link to="/accounts" class={buttonVariants({ variant: "default" })}>
                    {t(msg`Create your first account`)}
                  </Link>
                }
              />
            }
          >
            <ul class="divide-y divide-border/40">
              <For each={visibleWallets()}>
                {(walletRow, index) => (
                  <li
                    class="flex animate-scale-in items-center justify-between py-2.5"
                    style={{
                      "animation-delay": `${Math.min(index() * ROW_STAGGER_MS, ROW_STAGGER_MAX_MS)}ms`,
                    }}
                  >
                    <span class="flex items-center gap-2.5 text-sm">
                      <Wallet class="h-4 w-4 text-muted-foreground" />
                      {walletRow.name}
                    </span>
                    <span class="text-sm font-semibold font-numeric">
                      {formatMoney(
                        balanceByWallet().get(walletRow.id) ?? walletRow.initial_balance ?? 0,
                        walletRow.currency ?? baseCurrency(),
                        locale(),
                        { signDisplay: "exceptZero" },
                      )}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </CardContent>
      </Card>

      {/* ── Accesos rápidos ───────────────────────────────────────── */}
      <div class="grid gap-3 sm:grid-cols-3">
        <Link
          to="/transactions"
          class="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-4 text-sm font-medium transition-[transform,border-color,background-color] duration-150 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-muted/30 active:scale-[0.98] will-change-transform"
        >
          <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ArrowRightLeft class="h-5 w-5" />
          </span>
          {t(msg`Transactions`)}
        </Link>
        <Link
          to="/accounts"
          class="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-4 text-sm font-medium transition-[transform,border-color,background-color] duration-150 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-muted/30 active:scale-[0.98] will-change-transform"
        >
          <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Wallet class="h-5 w-5" />
          </span>
          {t(msg`Accounts`)}
        </Link>
        <Link
          to="/reports"
          class="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-4 text-sm font-medium transition-[transform,border-color,background-color] duration-150 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-muted/30 active:scale-[0.98] will-change-transform"
        >
          <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ChartPie class="h-5 w-5" />
          </span>
          {t(msg`Reports`)}
        </Link>
      </div>
    </PageContainer>
  );
}
